import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { enumToCheckboxItems, formatAddress, toTitleCase, unwrapConnection } from "@utils/utils";
import { format } from "date-fns";
import parsePhoneNumberFromString from "libphonenumber-js";
import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Breadcrumb, BreadcrumbLink, Button, Headline, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  ApiClinicListingVerificationStatusChoices,
  ClinicalServiceType,
  ModalityType,
  useGetClinicsQuery,
} from "@api/graphql/types-and-hooks";
import {
  PayorsEnum,
  useCareNavigationCancelOrgVerificationCreate,
  useCareNavigationVerifyOrgListingCreate,
} from "@api/rest/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { renderClinicStatus } from "@pages/Clinic/Clinics";

export const ClinicDetail: React.FC = () => {
  const { clinicId } = useParams<{ clinicId: string }>();
  const navigate = useNavigate();
  const numericId = clinicId ? atob(clinicId).split(":").pop() : undefined;

  const { data, isLoading, refetch } = useGetClinicsQuery(
    { clinicId: numericId },
    { queryKey: ["getClinic", clinicId], enabled: !!clinicId },
  );

  const { mutateAsync: cancelVerification } = useCareNavigationCancelOrgVerificationCreate();
  const { mutateAsync: verifyOrgListing } = useCareNavigationVerifyOrgListingCreate();

  if (isLoading) return;

  const clinics = unwrapConnection(data?.clinics);
  const clinic = clinics?.[0];
  const clincListing = clinic?.cliniclistingSet.edges[0]?.node;

  const cleanWebsite = clinic?.cliniclistingSet?.edges?.[0]?.node?.websiteUrl
    ?.replace(/^https?:\/\//, "")
    .replace(/\/$/, "");

  const breadcrumbLinks = [
    {
      type: "component",
      name: "Clinics",
      current: false,
      Component: (
        <Link to="/clinics" className="text-text-secondary">
          Clinics
        </Link>
      ),
    },
    {
      type: "component",
      name: clinic?.name || "...",
      current: true,
      Component: <span className="text-text-secondary">{clinic?.name || "..."}</span>,
    },
  ] as BreadcrumbLink[];

  const handleSubmitVerification = async () => {
    if (!clincListing || !clincListing.id) {
      notifyError("Clinic listing not found. Please try again.");
      return;
    }

    try {
      const response = await verifyOrgListing({ data: { clinic_listing_id: clincListing.id } });
      if (response.status !== 200) {
        notifyError(response.data.detail || "Failed to submit verification. Please try again.");
        return;
      }
      notifySuccess("Verification request submitted.");
      refetch();
    } catch {
      notifyError("Failed to verify. Please try again.");
    }
  };

  const handleCancel = async () => {
    if (!clincListing || !clincListing.id) {
      notifyError("Clinic listing not found. Please try again.");
      return;
    }

    try {
      const response = await cancelVerification({ data: { clinic_listing_id: clincListing.id } });
      if (response.status !== 200) {
        notifyError(response.data.detail || "Failed to cancel verification. Please try again.");
        return;
      }
      notifySuccess("Verification request cancelled.");
      refetch();
    } catch {
      notifyError("Failed to cancel. Please try again.");
    }
  };

  return (
    <div className="flex flex-col items-center mt-8 wrap-break-word">
      <div className="flex flex-col gap-2 max-w-6xl">
        <Breadcrumb
          links={breadcrumbLinks}
          homeLink={{
            name: "Home",
            Component: (
              <Link className="text-text-secondary" to={`/`}>
                Home
              </Link>
            ),
            current: false,
            type: "component",
          }}
        />

        <div className="flex flex-row items-center gap-2">
          <Headline>{clinic?.name}</Headline>
          <div>{renderClinicStatus(clincListing?.verificationStatus || null)}</div>
        </div>

        <Paragraph size="small">Review your clinic details</Paragraph>

        <div className="grid grid-cols-4 gap-8">
          <div className="flex flex-col col-span-3 gap-4 mt-6 p-8 bg-white border rounded-xl">
            <div className="flex flex-row items-center justify-between">
              <Headline>Clinic Information</Headline>
              {clincListing?.verificationStatus === ApiClinicListingVerificationStatusChoices.Verified && (
                <Button
                  text="Edit"
                  variant="primary"
                  buttonStyle="border"
                  size="sm"
                  onClick={() => navigate(`/clinics/${clinicId}/edit`)}
                />
              )}
            </div>

            <div className="flex flex-col">
              <Label size="small" colorType="secondary">
                Name
              </Label>
              <Paragraph colorType="primary">{clinic?.name}</Paragraph>
            </div>

            {clincListing?.description && (
              <div className="flex flex-col">
                <Label size="small" colorType="secondary">
                  About
                </Label>
                <Paragraph colorType="primary">{clincListing.description}</Paragraph>
              </div>
            )}

            <div className="flex flex-col">
              <Label size="small" colorType="secondary">
                Address
              </Label>
              <Paragraph colorType="primary">
                {formatAddress({
                  addressLine1: toTitleCase(clinic?.location?.addressLine1) || undefined,
                  addressLine2: toTitleCase(clinic?.location?.addressLine2) || undefined,
                  city: toTitleCase(clinic?.location?.city),
                  state: clinic?.location?.state,
                  zip: clinic?.location?.postalCode,
                })}
              </Paragraph>
            </div>

            <div className="flex flex-col">
              <Label size="small" colorType="secondary">
                Website
              </Label>
              <Paragraph colorType="primary">{cleanWebsite}</Paragraph>
            </div>

            {clincListing?.phoneNumber && (
              <div className="flex flex-col">
                <Label size="small" colorType="secondary">
                  Phone
                </Label>
                <Paragraph colorType="primary">
                  {parsePhoneNumberFromString(clincListing.phoneNumber, "US")?.format("NATIONAL") ||
                    clincListing?.phoneNumber}
                </Paragraph>
              </div>
            )}

            {clincListing?.contactEmail && (
              <div className="flex flex-col">
                <Label size="small" colorType="secondary">
                  Email
                </Label>
                <Paragraph colorType="primary">{clincListing.contactEmail}</Paragraph>
              </div>
            )}

            {clincListing?.servicesOffered && clincListing.servicesOffered.length > 0 && (
              <div className="flex flex-col gap-1">
                <Label size="small" colorType="secondary">
                  Services
                </Label>
                <div className="flex flex-row gap-2 flex-wrap">
                  {clincListing.servicesOffered.map((service) => (
                    <div key={service} className="p-2 rounded-3xl bg-limestone-50">
                      <Paragraph colorType="secondary">
                        {enumToCheckboxItems(ClinicalServiceType).find((i) => i.id === service)?.title}
                      </Paragraph>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {clincListing?.modalityTypes && clincListing.modalityTypes.length > 0 && (
              <div className="flex flex-col gap-1">
                <Label size="small" colorType="secondary">
                  Modalities
                </Label>
                <div className="flex flex-row gap-2 flex-wrap">
                  {clincListing.modalityTypes.map((modality) => (
                    <div key={modality} className="p-2 rounded-3xl bg-limestone-50">
                      <Paragraph colorType="secondary">
                        {enumToCheckboxItems(ModalityType).find((i) => i.id === modality)?.title}
                      </Paragraph>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {clincListing?.payors && clincListing.payors.length > 0 && (
              <div className="flex flex-col gap-1">
                <Label size="small" colorType="secondary">
                  Insurance accepted
                </Label>
                <div className="flex flex-row gap-2 flex-wrap">
                  {clincListing.payors.map((payor) => (
                    <div key={payor} className="p-2 rounded-3xl bg-limestone-50">
                      <Paragraph colorType="secondary">
                        {enumToCheckboxItems(PayorsEnum).find((i) => i.id === payor)?.title}
                      </Paragraph>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-6 p-8 bg-white border rounded-xl self-start">
            <Headline>Request Status</Headline>

            {clincListing?.verificationStatus === ApiClinicListingVerificationStatusChoices.Verified ? (
              <>
                <Paragraph colorType="secondary">Your clinic has been verified.</Paragraph>
                <Button
                  onClick={() => (window.location.href = "https://t9vgj.share.hsforms.com/2pqFEq-68RIi5xoNoS0oqsA")}
                  text={
                    <span className="flex flex-row items-center gap-2">
                      <EnvelopeIcon className="size-6" />
                      Contact Support
                    </span>
                  }
                  variant="primary"
                  buttonStyle="filled"
                  size="md"
                />
              </>
            ) : clincListing?.verificationStatus === ApiClinicListingVerificationStatusChoices.Pending ? (
              <>
                <div>
                  <Paragraph colorType="secondary">Request submitted</Paragraph>
                  <Paragraph colorType="primary">
                    {format(new Date(clincListing.lastUpdatedDate.toString()), "MMMM d, yyyy, h:mmaaa")}
                  </Paragraph>
                </div>
                <Button
                  onClick={() => (window.location.href = "https://t9vgj.share.hsforms.com/2pqFEq-68RIi5xoNoS0oqsA")}
                  text={
                    <span className="flex flex-row items-center gap-2">
                      <EnvelopeIcon className="size-6" />
                      Contact Support
                    </span>
                  }
                  variant="primary"
                  buttonStyle="filled"
                  size="md"
                />

                <Button text="Request Cancellation" variant="negative" buttonStyle="border" onClick={handleCancel} />
              </>
            ) : (
              <>
                <Paragraph colorType="secondary">You have not submitted verification for this clinic yet.</Paragraph>
                <Button
                  onClick={() => (window.location.href = "https://t9vgj.share.hsforms.com/2pqFEq-68RIi5xoNoS0oqsA")}
                  text={
                    <span className="flex flex-row items-center gap-2">
                      <EnvelopeIcon className="size-6" />
                      Contact Support
                    </span>
                  }
                  size="md"
                  buttonStyle="border"
                  variant="primary"
                />
                <Button text="Submit Verification" variant="primary" onClick={handleSubmitVerification} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
